import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import { TextInput } from '@interstate/components/TextInput';
import { Button } from '@interstate/components/Button';
import { Box } from '@interstate/components/Box';
import { ActionWithErrorBadge, dealLockHooks } from '@makemydeal/dr-dash-components';
import { FloppyDiskSolidIcon } from '@interstate/components/Icons';
import { Action } from '@interstate/components/Action';
import { CheckBox } from '@interstate/components/CheckBox';
import { useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';

import { useManualVehicleProtection } from './VehicleProtectionManualContext';
import { DATA_TESTID_INPUT_PREFIX } from './constants';
import { useVehicleProtectionContext } from '../../context/VehicleProtectionContext';
import { CatalogTableColumns } from '../../constants';
import type { EditableManualTableColumns } from './types/table';
import {
    MANUAL_VPP_EDIT_CELL_CLASSNAME,
    VehicleProtectionTableActions,
    VppTableCheckBoxContainer
} from '../vehicleProtectionTable/VehicleProtectionTable.style';
import { getManualVppMarkupValue } from '../../utils/vehicleProtectionTableUtils';
import { useHasVppProductError } from '../../utils/useHasVppProductError';

type ManualVppCellProps = {
    name: CatalogTableColumns;
    draftProduct: DraftDealProduct;
    displayValue?: JSX.Element;
};

export function ManualVppTableCell({ name, draftProduct, displayValue }: ManualVppCellProps) {
    const productId = draftProduct.id || '';
    const isLocked = dealLockHooks.useIsDealLocked();
    const { isDarwinVPPEditDisabled } = useVehicleProtectionContext();
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const hasProductError = useHasVppProductError();

    const { editableManualProductIds, onManualProductEdit, onManualProductSave, getManualProductById, getTableRowByProductId } =
        useManualVehicleProtection();

    const isEditable = editableManualProductIds.includes(productId) || false;
    const manualProduct = getManualProductById(productId);
    const tableRow = getTableRowByProductId(productId);

    if (!manualProduct || !tableRow) return null;

    switch (name) {
        case CatalogTableColumns.SELECT_PRODUCT:
            return (
                <VppTableCheckBoxContainer $isEditable={isEditable}>
                    <CheckBox
                        data-testid={`${manualProduct.selected ? 'selected' : 'unselected'}-product-checkbox-id`}
                        value={productId}
                        checked={manualProduct.selected}
                        disabled={true}
                    />
                </VppTableCheckBoxContainer>
            );
        case CatalogTableColumns.ACTIONS:
            if (isEditable) {
                return (
                    <VehicleProtectionTableActions>
                        <Action
                            actionIcon={{ icon: <FloppyDiskSolidIcon />, start: true }}
                            data-testid={`manual-vpp-delete-${productId}`}
                            onClick={() => onManualProductSave(productId)}
                            sx={{ paddingTop: '4px' }}
                            disabled={!manualProduct.isValid}
                        />
                        {tableRow.deleteAction}
                    </VehicleProtectionTableActions>
                );
            }
            return (
                <VehicleProtectionTableActions>
                    <Button
                        buttonStyle={'aux'}
                        size={'small'}
                        sx={{ verticalAlign: 'bottom' }}
                        data-testid="vpp-edit-button-action"
                        disabled={isLocked || isDarwinVPPEditDisabled}
                        onClick={() => onManualProductEdit(productId)}
                    >
                        {isGCDynamicValidationsEnabled ? (
                            <ActionWithErrorBadge
                                entityName={DealTabs.PROTECTION}
                                hasError={hasProductError(draftProduct)}
                                xOffset="-0.2rem"
                                yOffset="-0.15rem"
                            >
                                Edit
                            </ActionWithErrorBadge>
                        ) : (
                            'Edit'
                        )}
                    </Button>
                    {tableRow.deleteAction}
                </VehicleProtectionTableActions>
            );
        default:
            if (isEditable) {
                if (name === CatalogTableColumns.MARKUP_VALUE) {
                    const markupDisplayValue = getManualVppMarkupValue(manualProduct.productPrice, manualProduct.productCost);
                    return (
                        <Box className={MANUAL_VPP_EDIT_CELL_CLASSNAME}>
                            <TextInput
                                name={name}
                                size="small"
                                data-testid={`${DATA_TESTID_INPUT_PREFIX.MARKUP_VALUE}${productId}`}
                                displayLabel={false}
                                value={markupDisplayValue}
                                disabled={true}
                            />
                        </Box>
                    );
                }
                return (
                    <Box className={MANUAL_VPP_EDIT_CELL_CLASSNAME}>
                        {name in tableRow ? (
                            tableRow[name as EditableManualTableColumns]
                        ) : (
                            <TextInput name={name} size="small" displayLabel={false} value={'n/a'} disabled={true} />
                        )}
                    </Box>
                );
            }
            return displayValue;
    }
}
